<template>
  <ion-app>
    <ion-split-pane content-id="main-content">
      <Suspense>
        <AppMenu />
      </Suspense>
      <ion-router-outlet id="main-content"></ion-router-outlet>
    </ion-split-pane>
  </ion-app>
</template>

<script setup lang="ts">
import { IonApp, IonRouterOutlet, IonSplitPane } from '@ionic/vue';
import { storeToRefs } from 'pinia';
import {
  defineAsyncComponent,
  onMounted,
  onUnmounted,
  ref,
  watchEffect,
} from 'vue';

const AppMenu = defineAsyncComponent(() => import('@/components/AppMenu.vue'));

// Track tab visibility for PWA optimization
// When tab is hidden for an extended period, we unsubscribe from Firestore to reduce idle connections
const isTabVisible = ref(document.visibilityState === 'visible');

// Debounce delay before unsubscribing when tab is hidden (in ms)
// This prevents rapid subscribe/unsubscribe cycles when users quickly switch tabs
const UNSUBSCRIBE_DELAY_MS = 30000; // 30 seconds
let unsubscribeTimeoutId: ReturnType<typeof setTimeout> | undefined;

// Cleanup function set after stores are loaded, called by onUnmounted
let cleanupSubscriptions: (() => void) | undefined;

const handleVisibilityChange = () => {
  isTabVisible.value = document.visibilityState === 'visible';
};

document.addEventListener('visibilitychange', handleVisibilityChange);

// Lazy load and initialize singleton stores to reduce initial bundle size
// Auth store is initialized in AppMenu which is already loaded asynchronously
// Use onMounted to ensure Pinia is ready before accessing stores
onMounted(() => {
  void Promise.all([
    import('@/store/analytics').then(({ useAnalyticsStore }) =>
      useAnalyticsStore().init(),
    ),
    import('@/store/messaging').then(({ useMessagingStore }) =>
      useMessagingStore().init(),
    ),
    import('@/store/sw-update').then(({ useSwUpdateStore }) =>
      useSwUpdateStore().init(),
    ),
  ]);

  // Initialize Firestore subscriptions for global stores
  // These stores provide data needed across multiple routes
  void Promise.all([
    import('@/store/season'),
    import('@/store/team'),
    import('@/store/league'),
    import('@/store/game-week'),
    import('@/store/game-week-live'),
    import('@/store/roster'),
    import('@/store/leaderboard-position'),
    import('@/store/auth'),
  ]).then(
    ([
      { useSeasonStore },
      { useTeamStore },
      { useLeagueStore },
      { useGameWeekStore },
      { useGameWeekLiveStore },
      { useRosterStore },
      { useLeaderboardPositionStore },
      { useAuthStore },
    ]) => {
      const seasonStore = useSeasonStore();
      const teamStore = useTeamStore();
      const leagueStore = useLeagueStore();
      const gameWeekStore = useGameWeekStore();
      const gameWeekLiveStore = useGameWeekLiveStore();
      const rosterStore = useRosterStore();
      const leaderboardPositionStore = useLeaderboardPositionStore();
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);

      // Volatile stores: unsubscribe when tab is hidden to reduce idle connections
      // These have frequent external updates (live match points, leaderboard positions)
      const subscribeVolatileAuthStores = () => {
        leagueStore.subscribe();
        gameWeekLiveStore.subscribe();
        leaderboardPositionStore.subscribe();
      };

      const unsubscribeVolatileAuthStores = () => {
        leagueStore.unsubscribe();
        gameWeekLiveStore.unsubscribe();
        leaderboardPositionStore.unsubscribe();
      };

      // Persistent stores: stay subscribed even when tab is hidden
      // These are user-owned data or static data that other stores depend on
      // Keeping them subscribed avoids wiping pending roster changes on resubscribe
      const subscribePersistentAuthStores = () => {
        teamStore.subscribe();
        gameWeekStore.subscribe();
        rosterStore.subscribe();
      };

      const unsubscribePersistentAuthStores = () => {
        teamStore.unsubscribe();
        gameWeekStore.unsubscribe();
        rosterStore.unsubscribe();
      };

      // Subscribe seasonStore immediately - it's static data that never needs to unsubscribe
      seasonStore.subscribe();

      // Track whether stores are actually subscribed (for cleanup and avoiding double subscribe)
      let areVolatileAuthStoresSubscribed = false;
      let arePersistentStoresSubscribed = false;

      // Set cleanup function for onUnmounted (mainly for HMR during development)
      cleanupSubscriptions = () => {
        seasonStore.unsubscribe();
        if (areVolatileAuthStoresSubscribed) {
          unsubscribeVolatileAuthStores();
        }
        if (arePersistentStoresSubscribed) {
          unsubscribePersistentAuthStores();
        }
      };

      // Watch auth state and tab visibility to manage subscriptions
      watchEffect(() => {
        // Clear any pending unsubscribe timeout
        if (unsubscribeTimeoutId) {
          clearTimeout(unsubscribeTimeoutId);
          unsubscribeTimeoutId = undefined;
        }

        // Persistent stores: subscribe/unsubscribe based on auth only
        if (isAuthenticated.value && !arePersistentStoresSubscribed) {
          subscribePersistentAuthStores();
          arePersistentStoresSubscribed = true;
        } else if (!isAuthenticated.value && arePersistentStoresSubscribed) {
          unsubscribePersistentAuthStores();
          arePersistentStoresSubscribed = false;
        }

        // Volatile stores: should be subscribed when tab visible AND authenticated
        const shouldVolatileBeSubscribed =
          isTabVisible.value && isAuthenticated.value;

        if (shouldVolatileBeSubscribed && !areVolatileAuthStoresSubscribed) {
          subscribeVolatileAuthStores();
          areVolatileAuthStoresSubscribed = true;
        } else if (
          !shouldVolatileBeSubscribed &&
          areVolatileAuthStoresSubscribed
        ) {
          // Need to unsubscribe - debounce if tab hidden, immediate if logged out
          if (!isAuthenticated.value) {
            // Logged out - unsubscribe immediately
            unsubscribeVolatileAuthStores();
            areVolatileAuthStoresSubscribed = false;
          } else {
            // Tab hidden but still authenticated - debounce
            unsubscribeTimeoutId = setTimeout(() => {
              unsubscribeVolatileAuthStores();
              areVolatileAuthStoresSubscribed = false;
              unsubscribeTimeoutId = undefined;
            }, UNSUBSCRIBE_DELAY_MS);
          }
        }
      });
    },
  );
});

// Cleanup event listener, timeout, and subscriptions on unmount (e.g., during HMR)
onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange);

  // Clear any pending unsubscribe timeout
  if (unsubscribeTimeoutId) {
    clearTimeout(unsubscribeTimeoutId);
    unsubscribeTimeoutId = undefined;
  }

  // Unsubscribe from all stores (if they were loaded)
  cleanupSubscriptions?.();
});
</script>
